import { FC, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';

import { Recipient } from 'api/api';

import { FormLayout } from '~src/commons/components/layout/form-layout';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { Button } from '~src/ui/button';

import { GridItemWrapper } from '~address-form/components/grid-item-wrapper';
import { OptionalField } from '~address-form/components/optional-field';
import { AutocompleteField } from '~address-form/inputs/autocomplete-input';
import { isAddressSimilar } from '~address-form/utils/address-utils';

import { DynamicFieldRenderer } from '../components/dynamic-field-renderer';
import { useAddressFormBusinessLogic } from '../context/use-address-form';
import { HiddenSelectInput } from '../inputs/hidden-input';
import { FieldLayout, MetaAddressFormFields } from '../types';

interface DynamicAddressFormProps {
  address?: Recipient;
  formInstance: UseFormReturn<MetaAddressFormFields>;
  onSubmit: (newAddress: Recipient) => void;
  formButtons?: React.ReactNode;
  formExtension?: React.ReactNode;
  invalidComponents?: string[];
  onFormValuesChange: (address: Recipient, field: string) => void;
  onPlaceSelect: (address: Recipient) => void;
}

export const DynamicAddressForm: FC<DynamicAddressFormProps> = ({
  formInstance,
  address,
  onSubmit,
  formButtons,
  formExtension,
  invalidComponents,
  onFormValuesChange,
  onPlaceSelect,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();
  const { addressFormLayout } = useAddressFormBusinessLogic();
  const enableCompanyData = addressFormLayout.some(
    field => field.name === 'vat' || field.name === 'company_name'
  );

  const [companyDataVisible, setCompanyDataVisible] = useState<boolean>(
    !!(address?.company_name || address?.vat)
  );

  const callSubmit = (newAddress: Recipient) => {
    const addressToUpdate = isAddressSimilar(address, newAddress)
      ? { ...newAddress, coordinates: address!.coordinates }
      : newAddress;

    onSubmit(addressToUpdate);
  };

  const getFieldWarning = (fieldName: FieldLayout['name']) => {
    if (!globalState.address_form_configuration?.global_features?.enable_extended_validation) {
      return invalidComponents?.includes(fieldName.toUpperCase());
    }

    return undefined;
  };

  const getFieldLayout = (field: FieldLayout) => {
    if (!companyDataVisible && (field.name === 'company_name' || field.name === 'vat')) {
      return null;
    }

    if (field.name === 'address_lines') {
      return (
        <AutocompleteField
          key={field.name}
          onPlaceSelect={onPlaceSelect}
          invalidComponents={invalidComponents}
        />
      );
    }

    return (
      <GridItemWrapper key={field.name} span={field.span}>
        <DynamicFieldRenderer
          fieldType={field.type}
          fieldId={field.name}
          warning={getFieldWarning(field.name)}
        />
      </GridItemWrapper>
    );
  };

  return (
    <FormProvider {...formInstance}>
      <form
        css={{ height: '100%' }}
        onSubmit={formInstance.handleSubmit(callSubmit)}
        onChange={e => {
          onFormValuesChange(formInstance.getValues(), (e.target as HTMLInputElement).name);
        }}
      >
        {/* country value is non-editable by user, it's sole purpose is to point browser's address manager correct country
         * Is added only when `country` field is NOT present in the form layout
         */}
        <HiddenSelectInput
          name="country"
          values={[globalState.purchase_country]}
          autoComplete="country"
        />
        <FormLayout
          shrinkLayout
          submitButton={
            <div css={styles.buttonWrapper}>
              {formButtons ? (
                formButtons
              ) : (
                <Button type="submit" size="normal">
                  {tICU('widget.ADDRESS_MODAL.SAVE_BTN')}
                </Button>
              )}
            </div>
          }
        >
          <div css={styles.formWrapper}>
            {addressFormLayout.map(field => getFieldLayout(field))}
            <OptionalField
              visible={!enableCompanyData || companyDataVisible}
              label={tICU('widget.delivery_address.company_data.ADD_BTN')}
              onClick={() => setCompanyDataVisible(true)}
            />
          </div>
          {formExtension}
        </FormLayout>
      </form>
    </FormProvider>
  );
};

const styles = {
  formWrapper: {
    display: 'grid',
    gridRowGap: '12px',
    gridGap: '12px',
    gridTemplateColumns: 'repeat(12, 1fr)',
  },
  buttonWrapper: {
    marginTop: '16px;',
  },
};
